// @ts-strict-ignore
import { useEffect, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import {
  FileUploadField,
  LegacyInputField,
  MultiInputField,
  RecaptchaField,
  SelectField,
  SubmitButton,
  TextAreaField,
} from 'components/Form';
import { CmsApiSupportContactFormBlockReason } from 'modules/cms/api/types/CmsApiSupportContactForm';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { LOCALES } from 'utils/constants';

const ATTACHMENTS_MAX_LENGTH = 5;

type Props = {
  reasons: Array<CmsApiSupportContactFormBlockReason>;
  selectedReason: CmsApiSupportContactFormBlockReason | null | undefined;
  selectedSubReason: CmsApiSupportContactFormBlockReason | null | undefined;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateValue: (...args: Array<any>) => any;
};

export function CmsSupportContactFormBlockContactForm({
  reasons,
  selectedReason,
  selectedSubReason,
  updateValue,
}: Props) {
  const { user } = useUserEnvironment();
  const [subReasons, setSubReasons] = useState([]);
  const [subSubReasons, setSubSubReasons] = useState([]);
  useEffect(() => {
    if (selectedReason) {
      setSubReasons(selectedReason.subReasons || []);

      if (selectedReason.id === 'ORG' && selectedSubReason) {
        setSubSubReasons(selectedSubReason.subReasons || []);
      } else {
        setSubSubReasons([]);
        updateValue('subSubReason', undefined);
      }
    }
  }, [selectedReason, selectedSubReason, updateValue]);
  return (
    <>
      <LegacyInputField
        label={`${getText('Email to contact you')}*`}
        name="email"
      />
      <LegacyInputField label={`${getText('First Name')}*`} name="firstName" />
      <LegacyInputField label={`${getText('Last name')}*`} name="lastName" />
      <SelectField
        label={`${getText('Your language')}*`}
        name="locale"
        options={LOCALES.map((locale) => ({
          label: locale.name,
          value: locale.id,
        }))}
      />
      {user?.orgs.length > 0 ? (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          <SelectField
            label={`${getText('Which organization is this about?')}*`}
            name="orgName"
            options={[
              {
                label: `${getText('This is not about an organization')}`,
                value: '',
              },
            ].concat(
              user.orgs.map((org) => ({
                label: org.name,
                value: org.name,
              }))
            )}
          />
        </>
      ) : (
        <LegacyInputField
          label={`${getText(
            'Which organization is this regarding (if any)?'
          )}*`}
          name="orgName"
        />
      )}
      <SelectField
        label={`${getText('What is this about?')}*`}
        name="reason"
        options={[
          {
            label: `${getText('Please Select')}`,
            value: '',
          },
        ].concat(
          reasons.map((reason) => ({
            label: reason.text,
            value: reason.id,
          }))
        )}
      />
      {subReasons.length > 0 && (
        <SelectField
          label={`${getText('Narrow it down')}*`}
          name="subReason"
          options={[
            {
              label: `${getText('Please select')}`,
              value: '',
            },
          ].concat(
            subReasons.map((subReason) => ({
              label: subReason.text,
              value: subReason.id,
            }))
          )}
        />
      )}
      {subSubReasons.length > 0 && (
        <SelectField
          label={`${getText('Narrow it down')}*`}
          name="subSubReason"
          options={[
            {
              label: `${getText('Please select')}`,
              value: '',
            },
          ].concat(
            subSubReasons.map((subSubReason) => ({
              label: subSubReason.text,
              value: subSubReason.id,
            }))
          )}
        />
      )}
      <TextAreaField
        label={
          selectedReason && selectedReason.id === 'FEEDBACK'
            ? getText('Please describe your feedback in detail')
            : getText('How can we help?')
        }
        name="message"
      />

      <MultiInputField
        hideControls
        component={FileUploadField}
        maxInputs={ATTACHMENTS_MAX_LENGTH}
        name="attachmentIds"
        label={getText('Attachments')}
        description={sprintf(getText('Upload up to %(count)s attachments'), {
          count: ATTACHMENTS_MAX_LENGTH,
        })}
        extraData={new Array(ATTACHMENTS_MAX_LENGTH).fill({
          downloadAttachment: 'false',
          fileType: 'CONTACT_FORM',
          isRequired: false,
          label: '',
        })}
        extraAnalyticsData={new Array(ATTACHMENTS_MAX_LENGTH).fill({
          context: 'Support Contact Form',
          file_label: 'Attachments',
        })}
        showEmpty
      />
      <RecaptchaField />

      <Box mt={64} display="flex" justifyContent="flex-end">
        <SubmitButton size="large">{getText('Submit')}</SubmitButton>
      </Box>
    </>
  );
}
